import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ClipboardCheck, Star } from "lucide-react";

const RECS = [
  { v: "accept", l: "Accept" },
  { v: "minor_revision", l: "Minor Revision" },
  { v: "major_revision", l: "Major Revision" },
  { v: "reject", l: "Reject" },
];

function ReviewForm({ review, onDone }) {
  const [rec, setRec] = useState("");
  const [comments, setComments] = useState("");
  const [rating, setRating] = useState(3);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!rec || !comments.trim()) { toast.error("Please add a recommendation and comments."); return; }
    setSaving(true);
    try {
      await api.post(`/reviews/${review.id}/submit`, { recommendation: rec, comments, rating });
      toast.success("Review submitted. Thank you!");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Recommendation *</Label>
        <Select value={rec} onValueChange={setRec}>
          <SelectTrigger data-testid="review-recommendation" className="mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>{RECS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Rating</Label>
        <div className="flex gap-1 mt-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`rating-${n}`}>
              <Star className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label>Comments to editor *</Label>
        <Textarea data-testid="review-comments" value={comments} onChange={(e) => setComments(e.target.value)} rows={5} className="mt-1.5" placeholder="Your evaluation of the manuscript…" />
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="review-submit-btn" className="rounded-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Review"}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function ReviewerPanel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/my/reviews").then(({ data }) => setReviews(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="reviewer-panel">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1">Review Assignments</h1>
      <p className="text-slate-500 text-sm mb-6">Manuscripts assigned to you for peer review.</p>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-20 bg-card rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          <ClipboardCheck className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No review assignments yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100">{r.paper?.title}</h3>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{r.paper?.abstract}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-4">
                {r.status === "pending" ? (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="rounded-full" data-testid={`review-open-${r.id}`}>Submit Review</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                      <DialogHeader><DialogTitle className="font-serif">{r.paper?.title}</DialogTitle></DialogHeader>
                      {r.paper?.manuscript_text && (
                        <div className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line max-h-40 overflow-y-auto border rounded-lg p-3 bg-slate-50 dark:bg-slate-900">{r.paper.manuscript_text}</div>
                      )}
                      <ReviewForm review={r} onDone={load} />
                    </DialogContent>
                  </Dialog>
                ) : (
                  <div className="text-sm bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
                    <span className="font-medium capitalize">{r.recommendation?.replace("_", " ")}</span> · {r.rating}/5
                    <p className="text-slate-500 mt-1">{r.comments}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
